import React, { useState, useEffect, useRef } from 'react';
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import {
    Plus,
    Search,
    Filter,
    MoreVertical,
    Eye,
    Edit,
    Trash2,
    AlertCircle,
    MapPin,
    Tag,
    Shirt,
    Shield,
    Hammer,
    Package,
    HardHat,
    Zap,
    Wind,
    Thermometer,
    Droplets,
    Construction,
    AlertTriangle,
    Settings,
    CheckCircle2,
    Clock,
    RefreshCw,
    Check,
    XCircle,
    ChevronLeft,
    ChevronRight,
    DollarSign,
    Archive,
    RotateCcw,
    Camera,
    Loader2,
    Calendar,
    Minus
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '../../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogTrigger
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import api from '../../utils/api';
import { cn } from '../../components/ui/utils';
import { Can } from '../../components/Can';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { compressImage } from '../../utils/upload';
import { toast } from 'sonner';
import { toSydneyDate } from '../../utils/helpers';

interface InventoryItem {
    id: string;
    name: string;
    sku: string;
    size?: string;
    color?: string;
    currentStock: number;
    initialStockQuantity: number;
    costPerUnit: number;
    issuedCount: number;
    status: 'Draft' | 'Active' | 'Archived';
    photo?: string;
    category: { id: string; name: string, icon?: string, color?: string };
    supplier: { id: string; name: string; contactName?: string };
    location: { id: string; name: string };
    description?: string;
    internalNotes?: string;
    isActive: boolean;
    requiresApproval: boolean;
    trackExpiryDate: boolean;
    expiryDays: number;
    defaultLifespanDays: number;
    autoReorder: boolean;
    lowStockThreshold: number;
    reorderQuantity: number;
    issuanceLimit: number;
    incomingCount: number;
    createdAt?: string;
    isLinked?: boolean;
}

export const ICON_MAP: Record<string, any> = {
    Shirt, Shield, HardHat, Hammer, Package, Zap, Wind, Thermometer, Droplets, Construction, AlertTriangle, Tag
};

export const COLOR_MAP: Record<string, { bg: string, text: string, border: string }> = {
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-100' },
    orange: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-100' },
    green: { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-100' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-100' },
    red: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-100' },
    cyan: { bg: 'bg-cyan-50', text: 'text-cyan-600', border: 'border-cyan-100' },
    yellow: { bg: 'bg-yellow-50', text: 'text-yellow-600', border: 'border-yellow-100' },
    pink: { bg: 'bg-pink-50', text: 'text-pink-600', border: 'border-pink-100' },
    slate: { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-100' },
};

const InventoryList: React.FC = () => {
    const ability = useAbility(AbilityContext);
    const [items, setItems] = useState<InventoryItem[]>([]);
    const [categories, setCategories] = useState<any[]>([]);
    const [locations, setLocations] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedLocation, setSelectedLocation] = useState('all');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedStatus, setSelectedStatus] = useState('Active');

    // Modal states
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isCycleModalOpen, setIsCycleModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);

    useEffect(() => {
        fetchData();
    }, [search, selectedLocation, selectedCategory, selectedStatus]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const query = new URLSearchParams();
            if (search) query.append('q', search);
            if (selectedLocation !== 'all') query.append('locationId', selectedLocation);
            if (selectedCategory !== 'all') query.append('categoryId', selectedCategory);
            if (selectedStatus !== 'all') query.append('status', selectedStatus);

            const [itemsData, catsData, locsData] = await Promise.all([
                api.get<InventoryItem[]>(`/api/inventory/items?${query.toString()}`),
                api.get<any[]>('/api/inventory/categories'),
                api.get<any[]>('/api/inventory/locations')
            ]);

            setItems(itemsData);
            setCategories(catsData);
            setLocations(locsData);
        } catch (error) {
            console.error('Error fetching inventory data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRestoreItem = async (item: InventoryItem) => {
        try {
            await api.post(`/api/inventory/items/${item.id}/restore`, {});
            toast.success('Item restored successfully');
            fetchData();
        } catch (err: any) {
            toast.error(err.message || 'Failed to restore item');
        }
    };

    const handleDeleteItem = async () => {
        if (!selectedItem) return;
        const linked = selectedItem.isLinked || (selectedItem.issuedCount || 0) > 0 || (selectedItem.incomingCount || 0) > 0;
        try {
            if (linked && selectedItem.status !== 'Archived') {
                await api.post(`/api/inventory/items/${selectedItem.id}/archive`, {});
                toast.success('Item archived successfully');
            } else {
                await api.delete(`/api/inventory/items/${selectedItem.id}`);
                toast.success('Item deleted successfully');
            }
            fetchData();
            setIsDeleteModalOpen(false);
            setSelectedItem(null);
        } catch (err: any) {
            toast.error(err.message || `Failed to process request`);
        }
    };

    const isLowStock = (item: InventoryItem) => {
        return item.currentStock <= (item.lowStockThreshold || 20);
    };

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            {/* Title and Add Button */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-gray-900">Inventory Management</h2>
                    <p className="text-gray-500 text-sm font-medium">Manage uniform and PPE items across all locations</p>
                </div>
                {ability.can(ACTIONS.CREATE, RESOURCES.INVENTORY_ITEM) && (
                    <Button
                        onClick={() => { setSelectedItem(null); setIsAddModalOpen(true); }}
                        className="flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl h-10 px-4 font-bold shadow-lg shadow-slate-900/10 transition-all"
                    >
                        <Plus size={18} />
                        Add New Item
                    </Button>
                )}
            </div>

            {/* Filters */}
            <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 bg-white p-3 sm:p-4 rounded-[24px] sm:rounded-[28px] border border-gray-100 shadow-sm">
                <div className="relative flex-1 group">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-blue-500 transition-colors" size={18} />
                    <Input
                        placeholder="Search items by name or SKU..."
                        className="pl-12 rounded-2xl border-gray-100 bg-gray-50/50 h-11 transition-all focus:bg-white focus:ring-2 focus:ring-blue-500/10 font-medium text-sm"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-3">
                    <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                        <SelectTrigger className="flex-1 sm:w-[180px] rounded-2xl border-gray-100 bg-gray-50/50 h-11 font-bold text-xs">
                            <SelectValue placeholder="All Locations" />
                        </SelectTrigger>
                        <SelectContent className="rounded-2xl border-gray-100">
                            <SelectItem value="all">All Locations</SelectItem>
                            {locations.map((loc: any) => (
                                <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                        <SelectTrigger className="flex-1 sm:w-[150px] rounded-2xl border-gray-100 bg-gray-50/50 h-11 font-bold text-xs">
                            <SelectValue placeholder="All Categories" />
                        </SelectTrigger>
                        <SelectContent className="rounded-2xl border-gray-100">
                            <SelectItem value="all">All Categories</SelectItem>
                            {categories.map((cat: any) => (
                                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                        <SelectTrigger className="flex-1 sm:w-[130px] rounded-2xl border-gray-100 bg-gray-50/50 h-11 font-bold text-xs">
                            <div className="flex items-center gap-2">
                                <Archive size={14} className="text-gray-400 shrink-0" />
                                <SelectValue placeholder="Status" />
                            </div>
                        </SelectTrigger>
                        <SelectContent className="rounded-2xl border-gray-100">
                            <SelectItem value="Active">Active</SelectItem>
                            <SelectItem value="Archived">Archived</SelectItem>
                            <SelectItem value="all">All Status</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Inventory Content (Table for Desktop, Cards for Mobile) */}
            <div className="space-y-4">
                {/* Desktop Table View */}
                <div className="hidden lg:block bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden">
                    <Table>
                        <TableHeader className="bg-gray-50/50">
                            <TableRow className="border-b border-gray-100 hover:bg-transparent">
                                <TableHead className="w-[80px] font-bold text-gray-400 pl-6 uppercase text-[10px] tracking-wider">Photo</TableHead>
                                <TableHead className="font-bold text-gray-400 uppercase text-[10px] tracking-wider">Item Name</TableHead>
                                <TableHead className="font-bold text-gray-400 uppercase text-[10px] tracking-wider">Category</TableHead>
                                <TableHead className="font-bold text-gray-400 uppercase text-[10px] tracking-wider">Location</TableHead>
                                <TableHead className="font-bold text-gray-400 text-center uppercase text-[10px] tracking-wider">Stock</TableHead>
                                <TableHead className="font-bold text-gray-400 text-center uppercase text-[10px] tracking-wider">Issued</TableHead>
                                <TableHead className="font-bold text-gray-400 text-right uppercase text-[10px] tracking-wider">Cost</TableHead>
                                <TableHead className="w-[150px] text-right font-bold text-gray-400 pr-6 uppercase text-[10px] tracking-wider">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                [...Array(5)].map((_, i) => (
                                    <TableRow key={i} className="animate-pulse">
                                        <TableCell className="pl-6"><div className="w-10 h-10 bg-gray-50 rounded-xl" /></TableCell>
                                        <TableCell><div className="w-32 h-4 bg-gray-50 rounded-full" /></TableCell>
                                        <TableCell><div className="w-20 h-4 bg-gray-50 rounded-full" /></TableCell>
                                        <TableCell><div className="w-24 h-4 bg-gray-50 rounded-full" /></TableCell>
                                        <TableCell><div className="w-8 h-4 mx-auto bg-gray-50 rounded-full" /></TableCell>
                                        <TableCell><div className="w-8 h-4 mx-auto bg-gray-50 rounded-full" /></TableCell>
                                        <TableCell><div className="w-16 h-4 ml-auto bg-gray-50 rounded-full" /></TableCell>
                                        <TableCell />
                                    </TableRow>
                                ))
                            ) : items.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} className="h-64 text-center">
                                        <div className="flex flex-col items-center justify-center text-gray-400 py-12">
                                            <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-4">
                                                <Package size={32} className="opacity-20" />
                                            </div>
                                            <p className="text-lg font-bold text-gray-900">No inventory items found</p>
                                            <p className="text-sm">Try adjusting your filters or search terms</p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                items.map((item) => {
                                    const colorStyles = COLOR_MAP[item.category?.color || 'blue'] || COLOR_MAP.blue;
                                    const IconComp = ICON_MAP[item.category?.icon as string] || Tag;
                                    return (
                                        <TableRow key={item.id} className="group hover:bg-gray-50/50 transition-colors border-b border-gray-50 last:border-0">
                                            <TableCell className="pl-6 py-4">
                                                <div className="w-10 h-10 bg-gray-50 rounded-xl overflow-hidden border border-gray-100 flex items-center justify-center text-gray-300">
                                                    {item.photo ? <img src={item.photo} alt={item.name} className="w-full h-full object-cover" /> : <Package size={18} />}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="space-y-0.5">
                                                    <p className="font-bold text-sm text-gray-900 group-hover:text-blue-600 transition-colors">{item.name}</p>
                                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">SKU: {item.sku}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className={cn(
                                                    "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border-0 font-bold text-[10px] uppercase tracking-wider",
                                                    colorStyles.bg,
                                                    colorStyles.text
                                                )}>
                                                    <IconComp size={12} strokeWidth={2.5} />
                                                    {item.category?.name || 'Uncategorized'}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1.5 text-xs font-bold text-gray-600">
                                                    <MapPin size={12} className="text-gray-300" />
                                                    {item.location?.name || 'No Location'}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <div className="flex flex-col items-center">
                                                    <span className="font-bold text-sm text-gray-900">{item.currentStock}</span>
                                                    {item.currentStock === 0 ? (
                                                        <span className="text-[9px] font-black text-red-500 uppercase tracking-tighter">Out of Stock</span>
                                                    ) : isLowStock(item) && (
                                                        <span className="text-[9px] font-black text-red-500 uppercase tracking-tighter">Low Stock</span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-center font-bold text-sm text-gray-900">
                                                {item.issuedCount}
                                            </TableCell>
                                            <TableCell className="text-right font-black text-sm text-gray-900">
                                                ${parseFloat((item.costPerUnit || 0).toString()).toFixed(2)}
                                            </TableCell>
                                            <TableCell className="text-right pr-6">
                                                <div className="flex items-center justify-end gap-1.5">
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="w-8 h-8 rounded-lg border-gray-100 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-100 transition-all shadow-sm"
                                                        onClick={() => {
                                                            setSelectedItem(item);
                                                            setIsPreviewModalOpen(true);
                                                        }}
                                                        title="View Details"
                                                    >
                                                        <Eye size={14} />
                                                    </Button>
                                                    {/* <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="w-8 h-8 rounded-lg border-gray-100 hover:bg-amber-50 hover:text-amber-600 hover:border-amber-100 transition-all shadow-sm"
                                                        onClick={() => {
                                                            setSelectedItem(item);
                                                            setIsAddModalOpen(true);
                                                        }}
                                                        title="Edit Item"
                                                    >
                                                        <Edit size={14} />
                                                    </Button> */}
                                                    <Can I={ACTIONS.CYCLE} a={RESOURCES.INVENTORY_ITEM}>
                                                        <Button
                                                            variant="outline"
                                                            size="icon"
                                                            className="w-8 h-8 rounded-lg border-gray-100 hover:bg-slate-50 hover:text-slate-600 hover:border-slate-100 transition-all shadow-sm"
                                                            onClick={() => {
                                                                setSelectedItem(item);
                                                                setIsCycleModalOpen(true);
                                                            }}
                                                            title="Stock Cycling"
                                                        >
                                                            <RefreshCw size={14} />
                                                        </Button>
                                                    </Can>

                                                    {item.status === 'Archived' ? (
                                                        ability.can(ACTIONS.RESTORE, RESOURCES.INVENTORY_ITEM) && (
                                                            <Button
                                                                variant="outline"
                                                                size="icon"
                                                                className="w-8 h-8 rounded-lg border-gray-100 hover:bg-green-50 hover:text-green-600 hover:border-green-100 transition-all shadow-sm"
                                                                onClick={() => handleRestoreItem(item)}
                                                                title="Restore Item"
                                                            >
                                                                <RotateCcw size={14} />
                                                            </Button>
                                                        )
                                                    ) : (
                                                        ability.can(ACTIONS.DELETE, RESOURCES.INVENTORY_ITEM) && (
                                                            <Button
                                                                variant="outline"
                                                                size="icon"
                                                                className={cn(
                                                                    "w-8 h-8 rounded-lg border-gray-100 transition-all shadow-sm",
                                                                    (item.issuedCount > 0 || item.incomingCount > 0)
                                                                        ? "hover:bg-amber-50 hover:text-amber-600 hover:border-amber-100"
                                                                        : "hover:bg-red-50 hover:text-red-600 hover:border-red-100"
                                                                )}
                                                                onClick={() => {
                                                                    setSelectedItem(item);
                                                                    setIsDeleteModalOpen(true);
                                                                }}
                                                                title={(item.isLinked || item.issuedCount > 0 || item.incomingCount > 0) ? "Archive Item" : "Delete Item"}
                                                            >
                                                                {(item.isLinked || item.issuedCount > 0 || item.incomingCount > 0) ? <Archive size={14} /> : <Trash2 size={14} />}
                                                            </Button>
                                                        )
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </div>

                {/* Mobile Card View */}
                <div className="lg:hidden space-y-4">
                    {loading ? (
                        [...Array(3)].map((_, i) => (
                            <div key={i} className="bg-white p-5 rounded-3xl border border-gray-100 animate-pulse space-y-4">
                                <div className="flex items-center gap-4">
                                    <div className="w-16 h-16 bg-gray-50 rounded-2xl" />
                                    <div className="space-y-2">
                                        <div className="w-32 h-4 bg-gray-50 rounded-full" />
                                        <div className="w-20 h-3 bg-gray-50 rounded-full" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4 border-t border-gray-50 pt-4">
                                    <div className="h-10 bg-gray-50 rounded-xl" />
                                    <div className="h-10 bg-gray-50 rounded-xl" />
                                </div>
                            </div>
                        ))
                    ) : items.length === 0 ? (
                        <div className="bg-white rounded-[24px] border border-gray-100 p-12 text-center">
                            <div className="flex flex-col items-center justify-center text-gray-400">
                                <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-4">
                                    <Package size={32} className="opacity-20" />
                                </div>
                                <p className="text-lg font-bold text-gray-900">No inventory items found</p>
                                <p className="text-sm">Try adjusting your filters or search terms</p>
                            </div>
                        </div>
                    ) : (
                        items.map((item) => {
                            const colorStyles = COLOR_MAP[item.category.color || 'blue'] || COLOR_MAP.blue;
                            const IconComp = ICON_MAP[item.category.icon as string] || Tag;
                            return (
                                <div key={item.id} className="bg-white p-5 rounded-[24px] border border-gray-100 shadow-sm space-y-4">
                                    <div className="flex items-start justify-between">
                                        <div className="flex gap-4">
                                            <div className="w-12 h-12 bg-gray-50 rounded-xl overflow-hidden border border-gray-100 flex items-center justify-center text-gray-300 shrink-0">
                                                {item.photo ? <img src={item.photo} alt={item.name} className="w-full h-full object-cover" /> : <Package size={20} />}
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-gray-900">{item.name}</h3>
                                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">SKU: {item.sku}</p>
                                                <div className={cn(
                                                    "inline-flex items-center gap-1.5 px-2 py-0.5 mt-2 rounded-lg border-0 font-bold text-[9px] uppercase tracking-wider",
                                                    colorStyles.bg,
                                                    colorStyles.text
                                                )}>
                                                    <IconComp size={10} strokeWidth={2.5} />
                                                    {item.category?.name || 'Uncategorized'}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-black text-sm text-gray-900">${parseFloat(item.costPerUnit.toString()).toFixed(2)}</p>
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Cost</p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3 bg-gray-50/50 p-3 rounded-2xl">
                                        <div className="space-y-1">
                                            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Current Stock</p>
                                            <div className="flex items-center gap-2">
                                                <span className="font-bold text-gray-900">{item.currentStock}</span>
                                                {item.currentStock === 0 ? (
                                                    <span className="px-1.5 py-0.5 bg-red-100 text-red-600 rounded text-[8px] font-black uppercase">Out of Stock</span>
                                                ) : isLowStock(item) && (
                                                    <span className="px-1.5 py-0.5 bg-red-100 text-red-600 rounded text-[8px] font-black uppercase">Low</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Total Issued</p>
                                            <span className="font-bold text-gray-900">{item.issuedCount} Units</span>
                                        </div>
                                        <div className="col-span-2 flex items-center gap-2 pt-2 border-t border-gray-100/50">
                                            <MapPin size={10} className="text-gray-300" />
                                            <p className="text-xs font-bold text-gray-600">{item.location?.name || 'No Location'}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 pt-2">
                                        <Button
                                            variant="outline"
                                            className="flex-1 rounded-xl border-gray-100 hover:bg-blue-50 hover:text-blue-600 h-10 gap-2"
                                            onClick={() => {
                                                setSelectedItem(item);
                                                setIsPreviewModalOpen(true);
                                            }}
                                        >
                                            <Eye size={14} />
                                            View
                                        </Button>
                                        <Button
                                            variant="outline"
                                            className="flex-1 rounded-xl border-gray-100 hover:bg-amber-50 hover:text-amber-600 h-10 gap-2 text-[10px] font-bold"
                                            onClick={() => {
                                                setSelectedItem(item);
                                                setIsAddModalOpen(true);
                                            }}
                                        >
                                            <Edit size={14} />
                                            Edit
                                        </Button>
                                        <Can I={ACTIONS.CYCLE} a={RESOURCES.INVENTORY_ITEM}>
                                            <Button
                                                variant="outline"
                                                className="flex-1 rounded-xl border-gray-100 hover:bg-slate-50 hover:text-slate-600 h-10 gap-2 text-[10px] font-bold"
                                                onClick={() => {
                                                    setSelectedItem(item);
                                                    setIsCycleModalOpen(true);
                                                }}
                                            >
                                                <RefreshCw size={14} />
                                                Cycle
                                            </Button>
                                        </Can>
                                        {item.status === 'Archived' ? (
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                className="rounded-xl border-gray-100 hover:bg-green-50 hover:text-green-600 h-10 w-10 shrink-0"
                                                onClick={() => handleRestoreItem(item)}
                                            >
                                                <RotateCcw size={14} />
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                className={cn(
                                                    "rounded-xl border-gray-100 h-10 w-10 shrink-0",
                                                    (item.issuedCount > 0 || item.incomingCount > 0)
                                                        ? "hover:bg-amber-50 hover:text-amber-600"
                                                        : "hover:bg-red-50 hover:text-red-600"
                                                )}
                                                onClick={() => {
                                                    setSelectedItem(item);
                                                    setIsDeleteModalOpen(true);
                                                }}
                                            >
                                                {(item.isLinked || item.issuedCount > 0 || item.incomingCount > 0) ? <Archive size={14} /> : <Trash2 size={14} />}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Modals */}
            {isAddModalOpen && (
                <AddItemModal
                    isOpen={isAddModalOpen}
                    onClose={() => setIsAddModalOpen(false)}
                    onRefresh={fetchData}
                    categories={categories}
                    locations={locations}
                    itemToEdit={selectedItem}
                    mode="simple"
                />
            )}
            {isPreviewModalOpen && selectedItem && (
                <PreviewItemModal
                    isOpen={isPreviewModalOpen}
                    onClose={() => setIsPreviewModalOpen(false)}
                    item={selectedItem}
                />
            )}
            <DeleteConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDeleteItem}
                title={(selectedItem?.isLinked || (selectedItem?.issuedCount || 0) > 0 || (selectedItem?.incomingCount || 0) > 0) ? "Archive Inventory Item" : "Delete Inventory Item"}
                message={(selectedItem?.isLinked || (selectedItem?.issuedCount || 0) > 0 || (selectedItem?.incomingCount || 0) > 0)
                    ? `This item has existing records (orders, issuances, or logs) in the system. It will be archived instead of deleted to preserve historical data.`
                    : `Are you sure you want to delete "${selectedItem?.name}"? This action cannot be undone and will remove all stock and history records.`}
                actionType={(selectedItem?.isLinked || (selectedItem?.issuedCount || 0) > 0 || (selectedItem?.incomingCount || 0) > 0) ? "archive" : "delete"}
            />
            {isCycleModalOpen && selectedItem && (
                <StockCyclingModal
                    isOpen={isCycleModalOpen}
                    onClose={() => setIsCycleModalOpen(false)}
                    onRefresh={fetchData}
                    item={selectedItem}
                />
            )}
        </div>
    );
};

// --- Add/Edit Item Modal ---
export const AddItemModal = ({ isOpen, onClose, onRefresh, categories, locations, itemToEdit, mode = 'full' }: any) => {
    const isSimple = mode === 'simple';
    const [formData, setFormData] = useState({
        name: '',
        sku: '',
        categoryId: '',
        locationId: '',
        supplierId: '',
        size: '',
        color: '',
        costPerUnit: 0,
        initialStockQuantity: 0,
        currentStock: 0,
        issuanceLimit: 1,
        description: '',
        internalNotes: '',
        isActive: true,
        trackExpiryDate: false,
        expiryDays: 0,
        defaultLifespanDays: 0,
        autoReorder: false,
        lowStockThreshold: 0,
        reorderQuantity: 0,
        status: 'Active',
        photo: ''
    });
    const [suppliers, setSuppliers] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [photoPreview, setPhotoPreview] = useState<string>(itemToEdit?.photo || '');
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const fetchSuppliers = async () => {
            const data = await api.get('/api/inventory/suppliers');
            setSuppliers(data);
        };
        fetchSuppliers();
    }, []);

    useEffect(() => {
        return () => {
            if (photoPreview && photoPreview.startsWith('blob:')) {
                URL.revokeObjectURL(photoPreview);
            }
        };
    }, [photoPreview]);

    useEffect(() => {
        if (itemToEdit) {
            setFormData({
                name: itemToEdit.name || '',
                sku: itemToEdit.sku || '',
                categoryId: itemToEdit.category?.id || '',
                locationId: itemToEdit.location?.id || '',
                supplierId: itemToEdit.supplier?.id || '',
                size: itemToEdit.size || '',
                color: itemToEdit.color || '',
                costPerUnit: itemToEdit.costPerUnit || 0,
                initialStockQuantity: itemToEdit.initialStockQuantity || 0,
                currentStock: itemToEdit.currentStock || 0,
                issuanceLimit: itemToEdit.issuanceLimit || 1,
                description: itemToEdit.description || '',
                internalNotes: itemToEdit.internalNotes || '',
                isActive: itemToEdit.isActive ?? true,
                trackExpiryDate: itemToEdit.trackExpiryDate ?? false,
                expiryDays: itemToEdit.expiryDays || 0,
                defaultLifespanDays: itemToEdit.defaultLifespanDays || 0,
                autoReorder: itemToEdit.autoReorder ?? false,
                lowStockThreshold: itemToEdit.lowStockThreshold || 0,
                reorderQuantity: itemToEdit.reorderQuantity || 0,
                status: itemToEdit.status || 'Active',
                photo: itemToEdit.photo || ''
            });
            setPhotoPreview(itemToEdit.photo || '');
        }
    }, [itemToEdit]);

    const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validation
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Only JPEG, PNG, GIF and WebP images are allowed.');
            return;
        }

        try {
            setLoading(true);
            // Compress and resize the image
            // We'll limit it to 1024px maximum dimension and 0.7 quality
            const compressedFile = await compressImage(file, 1024, 1024, 0.7);

            if (compressedFile.size > 5 * 1024 * 1024) {
                toast.error('File size still exceeds 5MB limit.');
                return;
            }

            setSelectedFile(compressedFile);
            const previewUrl = URL.createObjectURL(compressedFile);
            setPhotoPreview(previewUrl);
        } catch (error) {
            console.error('Error processing image:', error);
            toast.error('Failed to process image');
        } finally {
            setLoading(false);
        }
    };

    const handleRemovePhoto = () => {
        setSelectedFile(null);
        setPhotoPreview('');
        setFormData(prev => ({ ...prev, photo: '' }));
    };

    const handleSubmit = async (submitStatus: string) => {
        try {
            setLoading(true);
            let finalPhotoUrl = formData.photo;

            // If a new file was selected, upload it first
            if (selectedFile) {
                const formDataUpload = new FormData();
                formDataUpload.append('photo', selectedFile);
                formDataUpload.append('itemName', formData.name || 'inventory-item');

                const uploadResponse = await api.post('/api/inventory/items/upload', formDataUpload);
                if (uploadResponse.success) {
                    finalPhotoUrl = uploadResponse.url;
                } else {
                    throw new Error(uploadResponse.message || 'Failed to upload photo');
                }
            }

            const data: any = { ...formData, photo: finalPhotoUrl, status: submitStatus };

            // Sanitize IDs - the backend expects integers/UUIDs and fails on empty strings
            if (!data.categoryId) data.categoryId = null;
            if (!data.locationId) data.locationId = null;
            if (!data.supplierId) data.supplierId = null;

            if (itemToEdit) {
                await api.patch(`/api/inventory/items/${itemToEdit.id}`, data);
                toast.success('Item updated successfully');
            } else {
                await api.post('/api/inventory/items', data);
                toast.success(`Item successfully ${submitStatus === 'Active' ? 'created' : 'saved as draft'}`);
            }
            onRefresh();
            onClose();
        } catch (error: any) {
            console.error('Error saving item:', error);
            toast.error(error.message || 'Failed to save item');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[95vh] rounded-[32px] p-0 overflow-hidden flex flex-col">
                <div className="p-6 border-b border-gray-50 flex-shrink-0">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-xl font-bold">
                            {itemToEdit ? <Edit className="text-gray-400" size={20} /> : <Plus className="text-gray-400" size={20} />}
                            {itemToEdit ? 'Edit Item' : 'Add New Item'}
                        </DialogTitle>
                    </DialogHeader>
                </div>

                <div className="p-6 sm:p-10 space-y-8 flex-1 overflow-y-auto custom-scrollbar">
                    {/* Photo Upload Area */}
                    <div className="flex flex-col items-center justify-center space-y-4">
                        <div className="relative group">
                            <div className="w-28 h-28 bg-gray-50 border-2 border-dashed border-gray-100 rounded-[32px] flex flex-col items-center justify-center text-gray-400 space-y-1 overflow-hidden transition-all group-hover:border-blue-400 group-hover:bg-blue-50/30 shadow-inner">
                                {photoPreview ? (
                                    <img src={photoPreview} alt="Item" className="w-full h-full object-cover" />
                                ) : (
                                    <>
                                        <Package size={28} className="opacity-20" />
                                        <span className="text-[10px] font-bold uppercase tracking-widest mt-1">Add Photo</span>
                                    </>
                                )}
                            </div>
                            {photoPreview && (
                                <button
                                    onClick={handleRemovePhoto}
                                    className="absolute -top-2 -right-2 w-7 h-7 bg-red-100 text-red-600 rounded-full flex items-center justify-center shadow-lg hover:bg-red-200 transition-colors border-2 border-white"
                                >
                                    <XCircle size={16} />
                                </button>
                            )}
                        </div>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handlePhotoChange}
                            accept="image/*"
                            className="hidden"
                        />
                        <Button
                            variant="outline"
                            className="rounded-xl flex items-center gap-2 text-xs font-bold shadow-sm h-10 px-6 hover:bg-gray-50 active:scale-95 transition-all border-gray-100"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={loading}
                        >
                            {loading && selectedFile ? (
                                <>
                                    <Loader2 size={14} className="animate-spin" />
                                    Uploading...
                                </>
                            ) : (
                                <>
                                    <Camera size={14} />
                                    {photoPreview ? 'Change Photo' : 'Capture Image'}
                                </>
                            )}
                        </Button>
                        <p className="text-[10px] text-gray-400 font-medium text-center leading-relaxed max-w-[280px]">
                            Accepts JPG, PNG, GIF, or WebP. Max 5MB.<br />
                            Square 1:1 format is recommended for best results.
                        </p>
                    </div>

                    <div className="space-y-10">
                        {/* Section 1: Basic Information */}
                        <div className="space-y-6">
                            <div className="flex items-center gap-2 text-sm font-black text-gray-900 uppercase tracking-widest bg-gray-50/50 w-fit px-4 py-2 rounded-xl">
                                <Tag size={16} className="text-blue-500" />
                                Core Identity
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Item Title *</label>
                                    <Input
                                        placeholder="e.g., Tactical Work Boots" className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 font-medium"
                                        value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Serial/SKU *</label>
                                    <Input
                                        placeholder="e.g., B-902-X" className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 font-medium"
                                        value={formData.sku} onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                                <div className="space-y-2 text-gray-500">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Category *</label>
                                    <Select value={formData.categoryId} onValueChange={(val) => setFormData({ ...formData, categoryId: val })}>
                                        <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 font-bold text-xs text-gray-900">
                                            <SelectValue placeholder="Select" />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-2xl border-gray-100 shadow-2xl">
                                            {categories.map((c: any) => {
                                                const IconComp = ICON_MAP[c.icon as string] || Tag;
                                                const colorStyle = COLOR_MAP[c.color] || COLOR_MAP.blue;
                                                return (
                                                    <SelectItem key={c.id} value={c.id}>
                                                        <div className="flex items-center gap-3">
                                                            <div className={cn("w-7 h-7 rounded-lg flex items-center justify-center shadow-sm", colorStyle.bg, colorStyle.text)}>
                                                                <IconComp size={14} strokeWidth={2.5} />
                                                            </div>
                                                            <span className="font-bold">{c.name}</span>
                                                        </div>
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Variant/Size</label>
                                    <Input
                                        placeholder="e.g., EU 42" className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 font-bold text-xs"
                                        value={formData.size} onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Color/Finish</label>
                                    <Input
                                        placeholder="e.g., Matte Black" className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 font-bold text-xs"
                                        value={formData.color} onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Public Description</label>
                                <textarea
                                    className="w-full h-24 rounded-2xl border-none bg-gray-50/50 p-4 text-sm focus:outline-none focus:ring-4 focus:ring-blue-500/5 transition-all font-medium placeholder:text-gray-300 shadow-inner"
                                    placeholder="Provide detailed information about the asset..."
                                    value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                />
                            </div>
                        </div>

                        {/* Section 2: Supplier & Location */}
                        {!isSimple && (
                            <div className="space-y-6">
                                <div className="flex items-center gap-2 text-sm font-black text-gray-900 uppercase tracking-widest bg-gray-50/50 w-fit px-4 py-2 rounded-xl">
                                    <MapPin size={16} className="text-orange-500" />
                                    Deployment & Source
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Primary Supplier *</label>
                                        <Select value={formData.supplierId} onValueChange={(val) => setFormData({ ...formData, supplierId: val })}>
                                            <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 font-bold text-xs">
                                                <SelectValue placeholder="Select Supplier" />
                                            </SelectTrigger>
                                            <SelectContent className="rounded-2xl border-gray-100">
                                                {suppliers.map((s: any) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Default Warehouse *</label>
                                        <Select value={formData.locationId} onValueChange={(val) => setFormData({ ...formData, locationId: val })}>
                                            <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 font-bold text-xs">
                                                <SelectValue placeholder="Select Location" />
                                            </SelectTrigger>
                                            <SelectContent className="rounded-2xl border-gray-100">
                                                {locations.map((l: any) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Section 3: Pricing & Inventory */}
                        {!isSimple && (
                            <div className="space-y-6">
                                <div className="flex items-center gap-2 text-sm font-black text-gray-900 uppercase tracking-widest bg-gray-50/50 w-fit px-4 py-2 rounded-xl">
                                    <DollarSign className="text-green-500" size={16} />
                                    Financials
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Unit Valuation ($HKD) *</label>
                                        <div className="relative group">
                                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 font-bold">$</span>
                                            <Input
                                                type="number" className="pl-8 rounded-2xl border-gray-100 bg-gray-50/50 h-12 font-black"
                                                value={formData.costPerUnit} onChange={(e) => setFormData({ ...formData, costPerUnit: parseFloat(e.target.value) })}
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Global Issuance Limit</label>
                                        <div className="relative group">
                                            <Input
                                                type="number" className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 font-black"
                                                value={formData.issuanceLimit} onChange={(e) => setFormData({ ...formData, issuanceLimit: parseInt(e.target.value) })}
                                            />
                                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[9px] font-black text-gray-300 uppercase">Max/User</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Section 4: Operational Config */}
                        {!isSimple && (
                            <div className="space-y-6">
                                <div className="flex items-center gap-2 text-sm font-black text-gray-900 uppercase tracking-widest bg-gray-50/50 w-fit px-4 py-2 rounded-xl">
                                    <Settings className="text-purple-500" size={16} />
                                    Operational Parameters
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Automatic Stock Lifespan</label>
                                        <div className="relative group">
                                            <Clock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-blue-500 transition-colors" size={14} />
                                            <Input
                                                type="number"
                                                min={0}
                                                className="pl-10 rounded-2xl border-gray-100 bg-gray-50/50 h-12 font-bold text-xs"
                                                placeholder="e.g., 365"
                                                value={formData.defaultLifespanDays}
                                                onChange={(e) => setFormData({ ...formData, defaultLifespanDays: parseInt(e.target.value) || 0 })}
                                            />
                                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[9px] font-black text-gray-300 uppercase">Days</span>
                                        </div>
                                    </div>
                                    <div className="flex items-end">
                                        <button
                                            onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                                            type="button"
                                            className={cn(
                                                "w-full flex items-center gap-3 p-3.5 rounded-2xl border transition-all text-left h-12",
                                                formData.isActive
                                                    ? "bg-blue-50/50 border-blue-100 text-blue-700 font-bold"
                                                    : "bg-gray-50/50 border-gray-100 text-gray-400 opacity-60 hover:opacity-100 font-bold"
                                            )}
                                        >
                                            <div className={cn(
                                                "w-5 h-5 rounded-lg flex items-center justify-center border transition-all",
                                                formData.isActive ? "bg-blue-600 border-blue-600 shadow-sm" : "bg-white border-gray-200"
                                            )}>
                                                {formData.isActive && <Check size={12} className="text-white" strokeWidth={4} />}
                                            </div>
                                            <span className="text-xs uppercase tracking-widest font-black">Item is Active</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Section 5: Internal Notes */}
                        {!isSimple && (
                            <div className="space-y-4">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Staff-Only Internal Notes</label>
                                <textarea
                                    className="w-full h-24 rounded-2xl border-none bg-gray-50/50 p-4 text-sm focus:outline-none focus:ring-4 focus:ring-blue-500/5 transition-all font-medium placeholder:text-gray-300 shadow-inner"
                                    placeholder="Private observations, specific storage instructions..."
                                    value={formData.internalNotes} onChange={(e) => setFormData({ ...formData, internalNotes: e.target.value })}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-6 bg-gray-50 border-t border-gray-100 flex gap-3 shrink-0">
                    <Button variant="ghost" className="flex-1 rounded-2xl h-14 font-black text-xs uppercase tracking-widest text-gray-400 hover:text-gray-900 transition-all" onClick={onClose}>Discard</Button>
                    <Button className="flex-[2] rounded-2xl h-14 bg-slate-900 hover:bg-slate-800 text-white font-black text-xs uppercase tracking-widest shadow-2xl shadow-slate-900/20 transition-all active:scale-[0.98]"
                        onClick={() => handleSubmit('Active')} disabled={loading}>
                        {loading ? <Loader2 size={18} className="animate-spin" /> : (itemToEdit ? 'Push Updates' : 'Deploy Item')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};


// --- Preview Modal ---
export const PreviewItemModal = ({ isOpen, onClose, item }: any) => {
    if (!item) return null;
    const IconComp = ICON_MAP[item.category?.icon] || Tag;
    const colorStyles = COLOR_MAP[item.category?.color || 'blue'] || COLOR_MAP.blue;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md rounded-[40px] p-0 overflow-hidden border-none shadow-2xl max-h-[95vh] flex flex-col [&>button:last-child]:text-white">
                <div className={cn("p-8 sm:p-10 text-white relative flex flex-col items-center justify-center gap-4 text-center overflow-hidden shrink-0", colorStyles.bg.replace('bg-', 'bg-').replace('-50', '-600'))}>
                    <div className="absolute inset-0 opacity-10 pointer-events-none">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -mr-20 -mt-20 blur-3xl opacity-20" />
                    </div>

                    <div className="w-20 h-20 sm:w-24 sm:h-24 bg-white/20 backdrop-blur-md rounded-[32px] flex items-center justify-center border border-white/20 shadow-xl relative z-10 transition-transform hover:scale-105 duration-300 overflow-hidden">
                        {item.photo ? (
                            <img src={item.photo} alt={item.name} className="w-full h-full object-cover" />
                        ) : (
                            <IconComp size={40} className="sm:size-12" strokeWidth={1.5} />
                        )}
                    </div>
                    <div className="relative z-10">
                        <Badge className="bg-white/20 text-white hover:bg-white/30 border-0 rounded-full px-3 py-1 font-bold text-[10px] tracking-[0.2em] uppercase mb-2 backdrop-blur-md">
                            {item.category?.name || 'Uncategorized'}
                        </Badge>
                        <h3 className="text-2xl sm:text-3xl font-black mb-1 tracking-tight">{item.name}</h3>
                        <p className="text-white/60 text-[10px] font-black uppercase tracking-[0.3em]">{item.sku}</p>
                    </div>
                </div>

                <div className="bg-white px-2 flex-1 overflow-y-auto min-h-0">
                    <div className="p-6 sm:p-8 space-y-6 sm:space-y-8 animate-in fade-in zoom-in-95 duration-300">
                        <div className="grid grid-cols-2 gap-3 sm:gap-4">
                            <div className="p-4 sm:p-5 bg-gray-50 rounded-3xl border border-gray-100/50 flex flex-col items-center text-center shadow-sm">
                                <p className="text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 text-blue-500/50">Current Stock</p>
                                <p className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tighter">{item.currentStock}</p>
                            </div>
                            <div className="p-4 sm:p-5 bg-gray-50 rounded-3xl border border-gray-100/50 flex flex-col items-center text-center shadow-sm">
                                <p className="text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2 text-blue-500/50">Unit Value</p>
                                <p className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tighter">${parseFloat(item.costPerUnit || 0).toFixed(2)}</p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center justify-between py-3 px-4 rounded-2xl bg-gray-50/50 border border-transparent hover:border-gray-100 transition-colors">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-xl bg-white border border-gray-100 flex items-center justify-center shadow-sm">
                                        <MapPin size={14} className="text-blue-500" />
                                    </div>
                                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Location</span>
                                </div>
                                <span className="font-bold text-sm text-gray-900">{item.location?.name || 'No Location'}</span>
                            </div>
                            <div className="flex items-center justify-between py-3 px-4 rounded-2xl bg-gray-50/50 border border-transparent hover:border-gray-100 transition-colors">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-xl bg-white border border-gray-100 flex items-center justify-center shadow-sm">
                                        <Package size={14} className="text-orange-500" />
                                    </div>
                                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Supplier</span>
                                </div>
                                <span className="font-bold text-sm text-gray-900 line-clamp-1 max-w-[150px] text-right">{item.supplier?.name || 'No Supplier'}</span>
                            </div>
                            <div className="flex items-center justify-between py-3 px-4 rounded-2xl bg-gray-50/50 border border-transparent hover:border-gray-100 transition-colors">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-xl bg-white border border-gray-100 flex items-center justify-center shadow-sm">
                                        <RefreshCw size={14} className="text-purple-500" />
                                    </div>
                                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Total Issued</span>
                                </div>
                                <span className="font-bold text-sm text-gray-900">{item.issuedCount} Units</span>
                            </div>
                            <div className="flex items-center justify-between py-3 px-4 rounded-2xl bg-gray-50/50 border border-transparent hover:border-gray-100 transition-colors">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-xl bg-white border border-gray-100 flex items-center justify-center shadow-sm">
                                        <Calendar size={14} className="text-green-500" />
                                    </div>
                                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Added Date</span>
                                </div>
                                <span className="font-bold text-sm text-gray-900">{toSydneyDate(item.createdAt) || 'N/A'}</span>
                            </div>
                        </div>
                    </div>

                    <div className="p-6 pt-0 shrink-0">
                        <Button onClick={onClose} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-[20px] h-14 font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-slate-900/20 transition-all active:scale-[0.98]">
                            Close Overview
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

// --- Stock Cycling Modal ---
interface StockCyclingModalProps {
    isOpen: boolean;
    onClose: () => void;
    onRefresh: () => void;
    item: any;
}

export const StockCyclingModal: React.FC<StockCyclingModalProps> = ({ isOpen, onClose, onRefresh, item }) => {
    const [loading, setLoading] = useState(false);
    const [targetQuantity, setTargetQuantity] = useState<number>(item.currentStock);
    const [reason, setReason] = useState('');

    const handleSubmit = async () => {
        const adjustmentValue = Math.abs(targetQuantity - item.currentStock);
        const type = targetQuantity >= item.currentStock ? 'increase' : 'decrease';

        if (targetQuantity < 0) {
            toast.error('Stock quantity cannot be negative');
            return;
        }

        if (adjustmentValue === 0) {
            toast.error('No changes detected');
            return;
        }

        if (!reason.trim()) {
            toast.error('Please provide a reason for the adjustment');
            return;
        }

        try {
            setLoading(true);
            await api.post(`/api/inventory/items/${item.id}/cycle`, {
                adjustment: adjustmentValue,
                type,
                reason
            });
            toast.success('Stock adjusted successfully');
            onRefresh();
            onClose();
        } catch (err: any) {
            toast.error(err.message || 'Failed to adjust stock');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md rounded-[32px] p-0 overflow-hidden border-0 shadow-2xl max-h-[95vh] flex flex-col">
                <div className="p-6 sm:p-8 space-y-8 bg-white flex-1 overflow-y-auto custom-scrollbar">
                    <div className="space-y-2">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                                <RefreshCw className="text-blue-500" size={20} />
                            </div>
                            <h2 className="text-2xl font-black text-gray-900 tracking-tight">Stock Cycling</h2>
                        </div>
                        <p className="text-sm font-medium text-gray-500 leading-relaxed">
                            Adjust manual stock levels for <span className="text-gray-900 font-bold">{item.name}</span>.
                        </p>
                    </div>

                    <div className="space-y-6">
                        {/* Item Summary */}
                        <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-xl border border-gray-100 flex items-center justify-center text-gray-300">
                                {item.photo ? <img src={item.photo} alt={item.name} className="w-full h-full object-cover rounded-xl" /> : <Package size={20} />}
                            </div>
                            <div>
                                <p className="font-bold text-gray-900 text-sm">{item.name}</p>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Available: {item.currentStock} Units</p>
                            </div>
                        </div>

                        {/* Adjustment Control */}
                        <div className="space-y-3">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">New Total Quantity</label>
                            <div className="flex items-center gap-3">
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="w-12 h-12 rounded-xl border-gray-100 hover:bg-red-50 hover:text-red-500 hover:border-red-100 transition-all shrink-0"
                                    onClick={() => setTargetQuantity(prev => Math.max(0, prev - 1))}
                                >
                                    <Minus size={20} />
                                </Button>

                                <div className="relative flex-1 group">
                                    <Input
                                        type="number"
                                        min={0}
                                        className="h-12 text-center rounded-xl border-gray-100 bg-gray-50/50 focus:ring-2 focus:ring-blue-500/10 font-black text-xl transition-all"
                                        value={targetQuantity}
                                        onChange={(e) => setTargetQuantity(parseInt(e.target.value) || 0)}
                                    />
                                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                                        <span className="text-[9px] font-black text-gray-300 uppercase">Units</span>
                                    </div>
                                </div>

                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="w-12 h-12 rounded-xl border-gray-100 hover:bg-green-50 hover:text-green-500 hover:border-green-100 transition-all shrink-0"
                                    onClick={() => setTargetQuantity(prev => prev + 1)}
                                >
                                    <Plus size={20} />
                                </Button>
                            </div>

                            {/* Diff Preview */}
                            <div className="flex items-center justify-between px-4 py-2 rounded-xl bg-gray-50 border border-gray-100 border-dashed">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Adjustment Change</span>
                                <div className={cn(
                                    "font-black text-sm",
                                    targetQuantity === item.currentStock ? "text-gray-400" :
                                        targetQuantity > item.currentStock ? "text-green-600" : "text-red-600"
                                )}>
                                    {targetQuantity === item.currentStock ? "No Change" :
                                        `${targetQuantity > item.currentStock ? '+' : '-'}${Math.abs(targetQuantity - item.currentStock)} Units`}
                                </div>
                            </div>
                        </div>

                        {/* Reason */}
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Reason for Adjustment</label>
                            <textarea
                                className="w-full h-24 rounded-2xl border-gray-100 bg-gray-50/50 p-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/10 transition-all font-medium placeholder:text-gray-300"
                                placeholder="Explain the reason for this manual adjustment..."
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                            />
                        </div>
                    </div>

                </div>

                <div className="flex items-center gap-3 p-6 bg-gray-50/50 border-t border-gray-50 mt-auto shrink-0">
                    <Button
                        variant="ghost"
                        onClick={onClose}
                        className="flex-1 rounded-xl h-12 font-bold text-gray-500 hover:bg-gray-100 transition-all"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={loading || targetQuantity === item.currentStock || !reason.trim()}
                        className="flex-[2] bg-blue-600 hover:bg-blue-700 text-white rounded-2xl h-12 font-bold shadow-lg shadow-blue-500/20 transition-all gap-2 disabled:opacity-50 disabled:shadow-none"
                    >
                        {loading ? (
                            <Loader2 size={18} className="animate-spin" />
                        ) : (
                            <>
                                <RefreshCw size={18} />
                                Update Stock
                            </>
                        )}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};


// --- Delete Confirm Modal ---
export const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, title, message, actionType = 'delete' }: any) => {
    const isArchive = actionType === 'archive';
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-sm rounded-[32px] p-8 border-none shadow-2xl overflow-hidden animate-in fade-in zoom-in-95">
                <div className="flex flex-col items-center text-center space-y-4">
                    <div className={cn(
                        "w-20 h-20 rounded-3xl flex items-center justify-center mb-2",
                        isArchive ? "bg-amber-50 text-amber-500" : "bg-red-50 text-red-500"
                    )}>
                        {isArchive ? <Archive size={48} strokeWidth={1.5} /> : <XCircle size={48} strokeWidth={1.5} />}
                    </div>
                    <div>
                        <h3 className="text-xl font-black text-gray-900">{title}</h3>
                        <p className="text-sm text-gray-500 mt-2 font-bold leading-relaxed px-2 uppercase tracking-tight opacity-70">{message}</p>
                    </div>
                </div>
                <div className="flex gap-3 mt-8">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        className="flex-1 rounded-2xl h-14 font-black text-[11px] uppercase tracking-wider border-gray-100 hover:bg-gray-50 transition-all"
                    >
                        Keep Item
                    </Button>
                    <Button
                        onClick={onConfirm}
                        className={cn(
                            "flex-1 rounded-2xl h-14 text-white font-black text-[11px] uppercase tracking-wider shadow-lg transition-all active:scale-95",
                            isArchive ? "bg-amber-500 hover:bg-amber-600 shadow-amber-600/20" : "bg-red-600 hover:bg-red-700 shadow-red-600/20"
                        )}
                    >
                        {isArchive ? 'Confirm Archive' : 'Confirm Delete'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default InventoryList;
